import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { ConfirmPopover } from '../library/ConfirmPopover'
import { useMediaSrc } from '../library/useMediaSrc'
import type { VaultItem } from '../library/vault/vaultData'
import { Comments } from './Comments'
import { EditPostSheet } from './EditPostSheet'
import { OfHtml } from './OfHtml'
import { applyEditPatch, buildPutPostBody, type EditPostPatch } from './editPost'
import { OF, type OfApi } from './ofApi'
import { Avatar } from './Avatar'
import type { OfUser, Post } from './posts'
import { PostStatsModal } from './PostStatsModal'
import { relativeTime } from './time'

export interface PostCardProps {
  post: Post
  author?: OfUser
  /** The Account's own OnlyFans user (its posts get the owner menu). */
  ownerId: number
  /** Console account id — keys the vault content picker when editing media. */
  accountId: string
  canWrite: boolean
  of: OfApi
  referer: string
  users: Record<number, OfUser>
  resolveUsers: (ids: number[]) => void
  onChange: (post: Post) => void
  /** Deleted or archived: gone from this list. */
  onRemove: (id: number, counters?: Record<string, unknown>) => void
  onPinned?: () => void
  onOpenMedia: (items: VaultItem[], index: number) => void
  say: (text: string) => void
  /** Extra ⋯ menu items for another creator's post (the Home feed); `close` shuts the menu. */
  feedMenu?: (close: () => void) => ReactNode
}

type Menu = null | 'menu' | 'delete' | 'edit' | 'stats'

/** One OnlyFans post as the OnlyFans profile and feed show it. */
export function PostCard(props: PostCardProps) {
  const { post, author, ownerId, accountId, canWrite, of, referer, onChange, onRemove, say } = props
  const { t } = useTranslation('ofPages')
  const [menu, setMenu] = useState<Menu>(null)
  const [commentsOpen, setCommentsOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const own = post.authorId === ownerId
  const paid = post.price > 0
  const hasPreview = paid && post.previewIds.length > 0
  // Another creator's unbought PPV: OnlyFans shows only the free previews, then the unlock box.
  const lockedIds = new Set(post.lockedIds)
  const visible = post.media.filter((m) => !lockedIds.has(m.id))
  const locked = post.media.filter((m) => lockedIds.has(m.id))
  const username = author?.username ?? ''
  const link = `${OF}/${post.id}/${username}`

  const run = async (work: () => Promise<void>) => {
    if (busy) return
    setBusy(true)
    try {
      await work()
    } catch (e) {
      say(e instanceof Error && e.message ? e.message : t('error'))
    } finally {
      setBusy(false)
    }
  }

  const like = () =>
    run(async () => {
      const r = await of.send<{ isFavorite?: boolean; favoritesCount?: number }>('POST', `posts/${post.id}/favorites/${post.authorId}`, referer)
      onChange({ ...post, isLiked: r?.isFavorite ?? !post.isLiked, likes: r?.favoritesCount ?? post.likes + (post.isLiked ? -1 : 1) })
    })

  const bookmark = () =>
    run(async () => {
      await of.send(post.isBookmarked ? 'DELETE' : 'POST', `posts/${post.id}/bookmarks`, referer)
      onChange({ ...post, isBookmarked: !post.isBookmarked })
      say(post.isBookmarked ? t('post.bookmarkRemoved') : t('post.bookmarked'))
    })

  const pin = () =>
    run(async () => {
      setMenu(null)
      await of.send('POST', `posts/${post.id}/pin`, referer, undefined, { format: 'status' })
      onChange({ ...post, isPinned: !post.isPinned })
      say(post.isPinned ? t('post.unpinned') : t('post.pinned'))
      props.onPinned?.()
    })

  const archive = (privately: boolean) =>
    run(async () => {
      setMenu(null)
      await of.send('POST', `posts/${post.id}/archive`, referer, undefined, privately ? { action: 'private_archive' } : {})
      onRemove(post.id)
      say(privately ? t('post.privateArchived') : t('post.archived'))
    })

  const remove = () =>
    run(async () => {
      setMenu(null)
      const r = await of.send<{ counters?: Record<string, unknown> }>('DELETE', `posts/${post.id}`, referer)
      onRemove(post.id, r?.counters)
      say(t('post.deleted'))
    })

  const copyLink = () => {
    setMenu(null)
    void navigator.clipboard?.writeText(link).then(
      () => say(t('linkCopied')),
      () => say(t('error')),
    )
  }

  const savePost = (patch: EditPostPatch) =>
    run(async () => {
      await of.send('PUT', `posts/${post.id}`, referer, buildPutPostBody(post, patch))
      onChange(applyEditPatch(post, patch))
      setMenu(null)
      say(t('post.saved'))
    })

  const ownerMenu = (
    <>
      <div className="lib-backdrop" onClick={() => setMenu(null)} />
      <div className="lib-pop lib-menu lib-menu--right" role="menu" aria-label={t('post.actions')}>
        <MenuItem label={t('post.copyLink')} onPress={copyLink} />
        {own && canWrite && <MenuItem label={post.isPinned ? t('post.unpin') : t('post.pin')} onPress={pin} />}
        {own && canWrite && post.canEdit && <MenuItem label={t('post.edit')} onPress={() => setMenu('edit')} />}
        {own && canWrite && <MenuItem label={t('post.archive')} onPress={() => archive(false)} />}
        {own && canWrite && <MenuItem label={t('post.privateArchive')} onPress={() => archive(true)} />}
        {own && canWrite && post.canDelete && <MenuItem label={t('post.delete')} danger onPress={() => setMenu('delete')} />}
        {own && <MenuItem label={t('post.detailedStats')} onPress={() => setMenu('stats')} />}
        {!own && props.feedMenu?.(() => setMenu(null))}
      </div>
    </>
  )

  return (
    <article className={`ofp-post ${busy ? 'is-busy' : ''}`} aria-label={t('post.label', { date: post.postedAt.slice(0, 10) })}>
      <header className="ofp-post-head">
        <Avatar user={author} size={44} />
        <div className="ofp-post-who">
          <span className="ofp-post-name">
            {author?.name ?? ''}
            {author?.verified && <FeatherIcon name="check-circle" size={13} className="ofp-verified" />}
          </span>
          <span className="ofp-post-handle">@{username}</span>
        </div>
        <span className="lib-spacer" />
        <div className="ofp-post-when">
          <time className="ofp-post-date" dateTime={post.postedAt} title={new Date(post.postedAt).toLocaleString()}>
            {relativeTime(post.postedAt, t)}
          </time>
          {own && paid && (
            <span className="ofp-post-price">
              {t('post.price', { price: post.price.toFixed(2) })}
              <FeatherIcon name="lock" size={12} />
            </span>
          )}
        </div>
        <div className="lib-anchor">
          <button type="button" className="lib-icon-btn" aria-label={t('post.actions')} onClick={() => setMenu(menu === 'menu' ? null : 'menu')}>
            <FeatherIcon name="more-horizontal" size={18} />
          </button>
          {menu === 'menu' && ownerMenu}
          {menu === 'delete' && (
            <ConfirmPopover title={t('post.deleteTitle')} body={t('post.deleteBody')} confirmLabel={t('post.deleteConfirm')} onConfirm={remove} onCancel={() => setMenu(null)} />
          )}
        </div>
      </header>
      {post.isPinned && (
        <div className="ofp-post-pin">
          <FeatherIcon name="bookmark" size={12} /> {t('post.pinnedBadge')}
        </div>
      )}
      {post.labels.length > 0 && (
        <div className="ofp-post-labels">
          <FeatherIcon name="tag" size={12} />
          <span>{t('post.labels')}</span>
          {post.labels.map((l) => (
            <span key={l.id} className="ofp-dot-item">
              {l.name}
            </span>
          ))}
        </div>
      )}
      <div className="ofp-post-body">
        <OfHtml html={post.text} />
        {own && hasPreview && <FreePreviewIcon className="ofp-post-free" title={t('post.freePreview')} />}
      </div>
      {visible.length > 0 && (
        <PostMedia items={visible} freeIds={own && hasPreview ? new Set(post.previewIds) : undefined} onOpen={(i) => props.onOpenMedia(visible, i)} />
      )}
      {locked.length > 0 && <PurchaseBox locked={locked} price={post.price} />}
      <footer className="ofp-post-tools">
        <button
          type="button"
          className={`ofp-tool ${post.isLiked ? 'is-on' : ''}`}
          aria-pressed={post.isLiked}
          aria-label={post.isLiked ? t('post.unlike') : t('post.like')}
          disabled={!canWrite || !post.canLike}
          onClick={like}
        >
          <FeatherIcon name="heart" size={20} />
        </button>
        <button
          type="button"
          className={`ofp-tool ${commentsOpen ? 'is-on' : ''}`}
          aria-expanded={commentsOpen}
          aria-label={t('post.comments')}
          onClick={() => setCommentsOpen((o) => !o)}
        >
          <FeatherIcon name="message-circle" size={20} />
        </button>
        {own && (
          <button type="button" className="ofp-tool ofp-tool--text" onClick={() => setMenu('stats')}>
            <FeatherIcon name="trending-up" size={18} />
            {t('post.statistics')}
          </button>
        )}
        <span className="lib-spacer" />
        <button
          type="button"
          className={`ofp-tool ${post.isBookmarked ? 'is-on' : ''}`}
          aria-pressed={post.isBookmarked}
          aria-label={post.isBookmarked ? t('post.removeBookmark') : t('post.bookmark')}
          disabled={!canWrite}
          onClick={bookmark}
        >
          <FeatherIcon name="bookmark" size={20} />
        </button>
      </footer>
      {(post.likes > 0 || post.comments > 0) && (
        <div className="ofp-post-counts">
          {[post.likes > 0 && t('post.likesCount', { count: post.likes }), post.comments > 0 && t('post.commentsCount', { count: post.comments })]
            .filter(Boolean)
            .join(' · ')}
        </div>
      )}
      {commentsOpen && (
        <Comments
          post={post}
          ownerId={ownerId}
          canWrite={canWrite}
          of={of}
          referer={referer}
          users={props.users}
          resolveUsers={props.resolveUsers}
          onCount={(n) => onChange({ ...post, comments: n })}
          say={say}
        />
      )}
      {menu === 'stats' && <PostStatsModal of={of} postId={post.id} referer={referer} onClose={() => setMenu(null)} />}
      {menu === 'edit' && (
        <EditPostSheet
          post={post}
          accountId={accountId}
          of={of}
          busy={busy}
          expireFrom={post.postedAt}
          onClose={() => setMenu(null)}
          onSave={(patch) => void savePost(patch)}
        />
      )}
    </article>
  )
}

export function MenuItem({ label, onPress, danger = false }: { label: string; onPress: () => void; danger?: boolean }) {
  return (
    <button type="button" role="menuitem" className={`lib-menu-item ${danger ? 'lib-menu-item--danger' : ''}`} onClick={onPress}>
      <span className="lib-menu-label">{label}</span>
    </button>
  )
}

function PostMedia({
  items,
  freeIds,
  onOpen,
}: {
  items: VaultItem[]
  freeIds?: Set<number>
  onOpen: (i: number) => void
}) {
  const { t } = useTranslation('ofPages')
  const shown = items.length === 1 ? items : items.slice(0, 4)
  return (
    <div className={`ofp-media ofp-media--${Math.min(shown.length, 4)}`}>
      {shown.map((m, i) => (
        <MediaThumb
          key={m.id}
          item={m}
          free={freeIds?.has(m.id)}
          big={items.length === 1}
          more={i === 3 && items.length > 4 ? items.length - 4 : 0}
          onOpen={() => onOpen(i)}
          label={t('post.openMedia')}
        />
      ))}
    </div>
  )
}

export function MediaThumb({
  item,
  big = false,
  more = 0,
  free = false,
  onOpen,
  label,
}: {
  item: VaultItem
  big?: boolean
  more?: number
  free?: boolean
  onOpen: () => void
  label: string
}) {
  const { t } = useTranslation('ofPages')
  const { ref, src, blocked } = useMediaSrc(big ? item.full || item.thumb : item.thumb || item.full)
  return (
    <button type="button" ref={ref as React.RefObject<HTMLButtonElement>} className="ofp-media-item" aria-label={label} onClick={onOpen}>
      {src ? <img src={src} alt="" /> : <span className={`ofp-media-ph ${blocked ? 'is-blocked' : ''}`} />}
      {item.type === 'video' && (
        <span className="ofp-media-play" aria-hidden="true">
          <FeatherIcon name="play" size={big ? 30 : 20} />
        </span>
      )}
      {free && (
        <span className="ofp-media-free" title={t('post.freePreview')}>
          <FreePreviewIcon title={t('post.freePreview')} />
        </span>
      )}
      {more > 0 && <span className="ofp-media-more">+{more}</span>}
    </button>
  )
}

/** OnlyFans' `icon-price-free` (a price tag struck through): marks a paid post's free preview. */
export function FreePreviewIcon({ className, title }: { className?: string; title: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" role="img" aria-label={title}>
      <path d="M21 10.6l-1.67-1.69-1.42 1.42L19.6 12a1.4 1.4 0 010 2L14 19.6a1.45 1.45 0 01-2 0l-1.68-1.69-1.41 1.42L10.6 21a3.4 3.4 0 004.8 0l5.6-5.6a3.4 3.4 0 000-4.8zM7.7 6a1.7 1.7 0 101.7 1.7A1.7 1.7 0 007.7 6zM22 3a1 1 0 00-1-1 1 1 0 00-.71.29l-3.79 3.8-2.91-2.92A4.06 4.06 0 0010.76 2H5a3 3 0 00-3 3v5.76a4 4 0 001.17 2.83l2.92 2.91-3.8 3.79A1 1 0 002 21a1 1 0 001 1 1 1 0 00.71-.29l18-18A1 1 0 0022 3zM7.5 15.09l-2.91-2.92A2 2 0 014 10.76V5a1 1 0 011-1h5.76a2 2 0 011.41.59l2.92 2.91z" />
    </svg>
  )
}

/** The grey box under another creator's unbought PPV: what is locked, the price, and OnlyFans' unlock button. */
function PurchaseBox({ locked, price }: { locked: VaultItem[]; price: number }) {
  const { t } = useTranslation('ofPages')
  const photos = locked.filter((m) => m.type === 'photo' || m.type === 'gif').length
  const videos = locked.filter((m) => m.type === 'video').length
  const audios = locked.filter((m) => m.type === 'audio').length
  const amount = t('post.price', { price: price.toFixed(2) })
  const parts: [string, number][] = [
    ['image', photos],
    ['video', videos],
    ['mic', audios],
  ]
  return (
    <div className="ofp-purchase">
      <div className="ofp-purchase-row">
        <span className="ofp-purchase-list" aria-label={t('post.lockedMedia', { count: locked.length })}>
          {parts
            .filter(([, n]) => n > 0)
            .map(([icon, n]) => (
              <span key={icon} className="ofp-dot-item">
                <FeatherIcon name={icon} size={13} /> {n}
              </span>
            ))}
        </span>
        <span className="lib-spacer" />
        {price > 0 && (
          <span className="ofp-purchase-price">
            {amount}
            <FeatherIcon name="lock" size={12} />
          </span>
        )}
      </div>
      {price > 0 && (
        <button type="button" className="ofp-purchase-btn" disabled title={t('post.unlockOnOf')}>
          {t('post.unlockFor', { price: amount })}
        </button>
      )}
    </div>
  )
}
